import _ from 'lodash';
import { getResourceTags, listDataLakeRoleProperty, listDataPermissions } from 'src/api/permissions';
import { scheduleBaseline, submitBaseline } from 'src/api/notifications';
import {
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_IAM_TYPE,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_REDSHIFT_ROLE_TO_USER_TYPE,
  DATA_PERMISSION_REDSHIFT_TYPE,
  DATA_PERMISSION_STATUS_ACTIVE,
  DATA_PERMISSION_STATUS_PENDING,
  LAKE_FORMATION_DATASOURCE_ID,
  S3_TABLE_DATASOURCE_ID,
} from 'src/commons/constants';
import { CardsProps } from '@amzn/awsui-components-react-v3/polaris/cards/interfaces';
import { Box, Button, Link, PaginationProps, SpaceBetween } from '@amzn/awsui-components-react-v3';
import * as React from 'react';
import {
  createCatalogDetailLink,
  createDatabaseDetailLink,
  createDatasetDetailLink,
  createGroupPermissionLFLink,
  createWSPermissionLFLink,
} from 'src/routes';
import { getAccountIdFromPrincipal, keyForCatalogNameMap } from 'src/commons/common';
import { isValidAccountId, isValidWorkspace } from 'src/commons/validationUtils';
import { QuestionnaireData } from 'src/components/permissions/myBaselining/baselineQuestionnaireForm';
import { DataPermissionList } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  DataCatalogResource,
  fetchFineGrainPoliciesOnDataset,
} from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { getIdInfoFromId } from 'src/components/utils/arnUtil';

export const BASELINE_RESOURCE_LIST_MAX_LENGTH = 50;
export const RESOURCE_USER_BASELINE = 'RESOURCE_USER_BASELINE';
export const BASELINE_TYPE_DATA_PERMISSION_CONSUMER = 'DATA_PERMISSION_CONSUMER';
export const BASELINE_ACTION_DELETE = 'DELETE';
export const BASELINE_ACTION_KEEP = 'KEEP';
export const BASELINE_2025_Q1_DUE_DATE = '2025-04-21'; // used as an extra filter to make sure we target specific DPs for MP first baseline

export const RESOURCE_TYPE_DATABASE = 'DATABASE';
export const RESOURCE_TYPE_TABLE = 'TABLE';
export const RESOURCE_TYPE_ALL_TABLES = 'ALL_TABLES';
export const RESOURCE_TYPE_SCHEMA = 'SCHEMA';
export const RESOURCE_TYPE_TAG = 'TAG';
export const RESOURCE_TYPE_FGAP = 'FGAP';
export const DP_RESOURCE_TYPE_EXT = 'EXTERNAL';

export const handleSelectedItem = (items, selectedItems) => {
  if (selectedItems.length == 0) {
    return [];
  } else {
    var realSelectedItems = [];
    for (var i = 0; i < selectedItems.length; i++) {
      for (var j = 0; j < items.length; j++) {
        if (_.isEqual(selectedItems[i], items[j])) {
          realSelectedItems.push(items[j]);
        }
      }
    }
    return realSelectedItems;
  }
};

export const getFieldOnChange =
  (fieldKey, onChangeFn) =>
  ({ detail: { selectedItems } }) => {
    onChangeFn({
      [fieldKey]: selectedItems,
    });
  };

export const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

export const escapeHtml = (string) => {
  const entityMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;',
  };
  return String(string).replace(/[&<>"'`=\/]/g, function (s) {
    return entityMap[s];
  });
};

export const TABLE_EMPTY_STATE = ({ resourceName }) => (
  <Box margin={{ vertical: 'xs' }} textAlign='center' color='inherit'>
    <SpaceBetween size='xxs'>
      <div>
        <b>No {resourceName.toLowerCase()}s</b>
        <Box variant='p' color='inherit'>
          No {resourceName.toLowerCase()}s found.
        </Box>
      </div>
    </SpaceBetween>
  </Box>
);

export const TABLE_NO_MATCH_STATE = (props) => (
  <Box margin={{ vertical: 'xs' }} textAlign='center' color='inherit'>
    <SpaceBetween size='xxs'>
      <div>
        <b>No matches</b>
        <Box variant='p' color='inherit'>
          We can't find a match.
        </Box>
      </div>
      <Button onClick={props.onClearFilter}>Clear filter</Button>
    </SpaceBetween>
  </Box>
);

export const ARIA_LABELS: CardsProps.AriaLabels<{ id: string }> = {
  itemSelectionLabel: (_data, row) => `select ${row.id}`,
  selectionGroupLabel: 'Items selection',
};

export const PAGINATION_ARIA_LABELS: (totalPages?: number) => PaginationProps.Labels = (totalPages) => ({
  nextPageLabel: 'Next page',
  previousPageLabel: 'Previous page',
  pageLabel: (pageNumber) => `Page ${pageNumber} of ${totalPages || 'all pages'}`,
});

export const getAllResourceTags = _.memoize(async (resource: DataCatalogResource) => {
  try {
    const tags = [];
    const baseReq = { statusCustomerType: 'Active:Publisher' };
    let result = await getResourceTags({ resourceType: 'Database', ...baseReq });
    tags.push(...result.tags);
    result = await getResourceTags({ resourceType: 'Dataset', ...baseReq });
    tags.push(...result.tags);
    result = await getResourceTags({ resourceType: 'Schema', ...baseReq });
    tags.push(...result.tags);

    return tags.filter(function (tag) {
      return tag.catalogId === resource.accountId && tag.region === resource.region;
    });
  } catch (e) {
    console.error(e);
    return [];
  }
});

export const submitBaselineWithQuestionnaire = async (questionnaireList: QuestionnaireData[]) => {
  let requests = buildSubmitBaselineWithQuestionnaireRequests(questionnaireList);
  await Promise.all(requests.map((request) => submitBaseline(request)));
};

export const buildSubmitBaselineWithQuestionnaireRequests = (questionnaireList: QuestionnaireData[]) => {
  let requests = [];
  for (let questionnaire of questionnaireList) {
    let originalData = questionnaire.originalData;
    let nextQuarter = questionnaire.nextQuarter;
    originalData.questionnaire[nextQuarter] = questionnaire.questionsAndAnswers;

    let updatedQuestionnaireJson = JSON.stringify(originalData);

    let dataPermissions = questionnaire.dataPermissions;

    if (dataPermissions.length > 0) {
      let chunkedDataPermissions = _.chunk(dataPermissions, BASELINE_RESOURCE_LIST_MAX_LENGTH);
      chunkedDataPermissions.forEach((dps) => {
        requests.push({
          baselineAction: BASELINE_ACTION_KEEP,
          baselineType: RESOURCE_USER_BASELINE,
          resourceType: BASELINE_TYPE_DATA_PERMISSION_CONSUMER,
          resourcesToBaseline: dps.map((dp) => {
            return {
              id: dp.dataPermissionId,
            };
          }),
          baselineInfo: { useCase: updatedQuestionnaireJson },
        });
      });
    }
  }
  return requests;
};

export const submitBaselineRevoke = async (dpToRevoke: DataPermissionList) => {
  let requests = buildSubmitBaselineRevokeRequests(dpToRevoke);
  await Promise.all(requests.map((request) => submitBaseline(request)));
};

export const buildSubmitBaselineRevokeRequests = (dpToRevoke: DataPermissionList) => {
  let requests = [];
  if (dpToRevoke.length > 0) {
    let chunkedDataPermissions = _.chunk(dpToRevoke, BASELINE_RESOURCE_LIST_MAX_LENGTH);
    chunkedDataPermissions.forEach((dps) => {
      requests.push({
        baselineAction: BASELINE_ACTION_DELETE,
        baselineType: RESOURCE_USER_BASELINE,
        resourceType: BASELINE_TYPE_DATA_PERMISSION_CONSUMER,
        resourcesToBaseline: dps.map((dp) => {
          return {
            id: dp.dataPermissionId,
          };
        }),
      });
    });
  }
  return requests;
};

export const recursiveFetchDataPermissions = async (request) => {
  const dps = [];
  let result = await listDataPermissions(request);
  dps.push(...result.dataPermissionList);
  while (result.nextToken) {
    request.nextToken = result.nextToken;
    result = await listDataPermissions(request);
    dps.push(...result.dataPermissionList);
  }
  return dps;
};

export const fetchBaselineNeededConsumerDataPermissions = async (ownerIds, filterAccountId = undefined) => {
  let dps = [];
  for (let ownerId of ownerIds) {
    dps.push(
      ...(await recursiveFetchDataPermissions({
        ownerId: ownerId,
        option: DATA_PERMISSION_CONSUMER_OPTION,
        baselineNeededOnly: true,
      })),
    );
  }
  if (filterAccountId) {
    dps = dps.filter((dp) => {
      let accountId = getAccountIdFromPrincipal(dp.dataLakePrincipal);
      return isValidAccountId(accountId) && accountId == filterAccountId;
    });
  }
  return dps;
};

export const fetchBaselineNeededConsumerIAMDataPermissions = async (groupId) => {
  let role;
  try {
    role = await listDataLakeRoleProperty({ groupId: groupId });
  } catch (error) {
    // Group doesn't have any IAM data permissions
    if ('NotFoundException' != error.code || 404 != error.status) throw error;
  }
  const iamDps = await recursiveFetchDataPermissions({
    ownerId: groupId,
    option: DATA_PERMISSION_CONSUMER_OPTION,
    type: DATA_PERMISSION_IAM_TYPE,
  });

  if (role && role.approvedPermissions.length > 0) {
    for (let iamDp of iamDps) {
      if (!iamDp.resource || !iamDp.resource.table || !iamDp.region) continue;
      const tableResource = iamDp.resource.table;
      const datasetId = `DS-glue|A-${tableResource.catalogId}|DN-${tableResource.databaseName}|TN-${tableResource.name}|R-${iamDp.region}`;
      const index = role.approvedPermissions.indexOf(datasetId);
      // IAM data permission already baselined and backfilled as DataPermission.
      if (index > -1) role.approvedPermissions.splice(index, 1);
    }

    // IAM data permissions that still require a baseline review.
    // Represent each table as single DataPermission
    const dps = [];
    for (let datasetId of role.approvedPermissions) {
      const subs = datasetId.split('|');
      const catalogId = subs[1].slice(2);
      const databaseName = subs[2].slice(3);
      const tableName = subs[3].slice(3);
      const region = subs[4].slice(2);

      dps.push({
        dataPermissionId: groupId,
        ownerId: groupId,
        region: region,
        resource: {
          table: {
            catalogId: catalogId,
            databaseName: databaseName,
            name: tableName,
          },
        },
        type: DATA_PERMISSION_IAM_TYPE,
        hcDatasetId: datasetId,
        baselineInfo: {
          baselineDueDate: undefined,
          baselineStatus: undefined,
          baselineStatusReason: undefined,
          lastBaselineBy: undefined,
          lastBaselineTimestamp: undefined,
        },
      });
    }
    return dps;
  }
  return [];
};

export const fetchBaselineNeededConsumerRedshiftRoleToUserDataPermissions = async (groupId) => {
  const dps = [];
  dps.push(
    ...(await recursiveFetchDataPermissions({
      ownerId: groupId,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      type: DATA_PERMISSION_REDSHIFT_ROLE_TO_USER_TYPE,
      status: DATA_PERMISSION_STATUS_PENDING,
    })),
  );
  dps.push(
    ...(await recursiveFetchDataPermissions({
      ownerId: groupId,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      type: DATA_PERMISSION_REDSHIFT_ROLE_TO_USER_TYPE,
      status: DATA_PERMISSION_STATUS_ACTIVE,
    })),
  );
  return dps;
};

export const fetchActiveConsumerDataPermissions = async (resource: DataCatalogResource) => {
  if (!resource || !resource.accountId || !resource.region || !resource.dpType) return [];

  let dps = [];
  const request = {
    region: resource.region,
    status: DATA_PERMISSION_STATUS_ACTIVE,
    option: DATA_PERMISSION_CONSUMER_OPTION,
    type: resource.dpType,
    resource: null,
  };
  const resourceType = resource.type;

  switch (resourceType) {
    case RESOURCE_TYPE_DATABASE:
      request['resource'] = {
        table: {
          catalogId: resource.accountId,
          bucketName: resource.dataCatalogObjectDetails.bucketName,
          databaseName: resource.dataCatalogObjectDetails.databaseName,
        },
      };
      break;
    case RESOURCE_TYPE_TABLE:
      request['resource'] = {
        table: {
          catalogId: resource.accountId,
          bucketName: resource.dataCatalogObjectDetails.bucketName,
          databaseName: resource.dataCatalogObjectDetails.databaseName,
          name: resource.dataCatalogObjectDetails.tableName,
        },
      };
      // fetch all table consumers (DB level) - they will also have access to these tables.
      const allTablesResource = { ...resource };
      allTablesResource.type = RESOURCE_TYPE_ALL_TABLES;

      const dataPermissionPromises = [fetchActiveConsumerDataPermissions(allTablesResource)];
      // fetch all FGAC consumers
      if (!!resource.idInfo) {
        const fgaPolicyList = await fetchFineGrainPoliciesOnDataset(resource.idInfo);
        if (fgaPolicyList && fgaPolicyList.length > 0) {
          const fgapPromises = fgaPolicyList.map((fgaPolicy) => {
            const fgapResource = { ...resource };
            fgapResource.type = RESOURCE_TYPE_FGAP;
            fgapResource.dataCatalogObjectDetails.fgapName = fgaPolicy?.tableData?.physicalName;
            return fetchActiveConsumerDataPermissions(fgapResource);
          });
          dataPermissionPromises.push(...fgapPromises);
        }
      }

      const results = await Promise.all(dataPermissionPromises);
      results.forEach((result) => dps.push(...result));
      break;
    case RESOURCE_TYPE_FGAP:
      request['resource'] = {
        dataCellsFilter: {
          tableCatalogId: resource.accountId,
          databaseName: resource.dataCatalogObjectDetails.databaseName,
          tableName: resource.dataCatalogObjectDetails.tableName,
          name: resource.dataCatalogObjectDetails.fgapName,
        },
      };
      break;
    case RESOURCE_TYPE_ALL_TABLES:
      request['resource'] = {
        table: {
          catalogId: resource.accountId,
          bucketName: resource.dataCatalogObjectDetails.bucketName,
          databaseName: resource.dataCatalogObjectDetails.databaseName,
          tableWildcard: {},
        },
      };
      break;
    case RESOURCE_TYPE_TAG:
      if (resource.dpType === 'LF') {
        request['resource'] = {
          lFTagPolicy: {
            expression: [
              {
                tagKey: resource.tagDetails.tagKey,
                tagValues: [resource.tagDetails.tagValue],
              },
            ],
            catalogId: resource.accountId,
          },
        };
      } else {
        request['resource'] = {
          redshiftTagPolicy: {
            tagKey: resource.tagDetails.tagKey,
            tagValue: resource.tagDetails.tagValue,
            catalogId: resource.accountId,
          },
        };
      }
      break;
  }

  if (
    DATA_PERMISSION_LAKE_FORMATION_TYPE == resource.dpType ||
    (DATA_PERMISSION_REDSHIFT_TYPE == resource.dpType && request.resource && request.resource.redshiftTagPolicy)
  ) {
    dps.push(...(await recursiveFetchDataPermissions(request)));
  }
  dps = dps.filter(function (dp) {
    return dp.resourceType && dp.resourceType === DP_RESOURCE_TYPE_EXT;
  });

  if (
    resourceType === RESOURCE_TYPE_DATABASE ||
    resourceType === RESOURCE_TYPE_TABLE ||
    resourceType === RESOURCE_TYPE_SCHEMA
  ) {
    const finalTags = [];
    let resourceTags = await getAllResourceTags(resource);
    finalTags.push(
      ...resourceTags.filter(function (tag) {
        return tag.resourceId.startsWith(resource.tagResourceId) && tag.resourceId.endsWith(resource.region);
      }),
    );
    if (resourceType === RESOURCE_TYPE_TABLE) {
      finalTags.push(
        ...resourceTags.filter(function (tag) {
          return tag.resourceId === resource.tagUpperLevelResourceId;
        }),
      );
    }

    for (let tag of finalTags) {
      const tagResource: DataCatalogResource = {
        type: RESOURCE_TYPE_TAG,
        accountId: resource.accountId,
        region: resource.region,
        dpType: resource.dpType,
        tagDetails: {
          tagKey: tag.tagKey,
          tagValue: tag.tagValue,
        },
      };
      dps.push(...(await fetchActiveConsumerDataPermissions(tagResource)));
    }
  }
  return dps;
};

export const submitResourceUserBaseline = async (baselineAction, resourceType, resourcesToBaseline) => {
  return await submitBaseline({
    baselineAction: baselineAction,
    baselineType: RESOURCE_USER_BASELINE,
    resourceType: resourceType,
    resourcesToBaseline: resourcesToBaseline,
  });
};

export const scheduleResourceUserBaseline = async (resource, endDate, message) => {
  return await scheduleBaseline({
    baselineType: RESOURCE_USER_BASELINE,
    resource: resource,
    scheduleInfo: {
      endDate: endDate,
      message: message,
    },
  });
};

const getDataObjectDetails = (dp) => {
  if (!dp || !dp.resource) return null;
  const dpResource = dp.resource;
  const database = dpResource.database;
  const table = dpResource.table;
  const tableWithColumns = dpResource.tableWithColumns;
  const lfTag = dpResource.lFTagPolicy;
  const redshiftTag = dpResource.redshiftTagPolicy;
  const dataCellsFilter = dpResource.dataCellsFilter;

  if (database) {
    return {
      catalogId: database.catalogId,
      bucketName: database.bucketName,
      databaseName: database.name,
      tableName: 'ALL_TABLES',
    };
  }

  if (table) {
    return {
      catalogId: table.catalogId,
      bucketName: table.bucketName,
      databaseName: table.databaseName,
      tableName: table.name ? table.name : 'ALL_TABLES',
      hcDatasetId: dp.hcDatasetId
        ? dp.hcDatasetId
        : table.bucketName?.length > 0
        ? `DS-s3table|A-${table.catalogId}|BN-${table.bucketName}|DN-${table.databaseName}|TN-${table.name}|R-${dp.region}`
        : `DS-glueLF|A-${table.catalogId}|DN-${table.databaseName}|TN-${table.name}|R-${dp.region}`,
    };
  }

  if (tableWithColumns) {
    return {
      catalogId: tableWithColumns.catalogId,
      bucketName: tableWithColumns.bucketName,
      databaseName: tableWithColumns.databaseName,
      tableName: tableWithColumns.name,
      hcDatasetId:
        tableWithColumns.bucketName?.length > 0
          ? `DS-s3table|A-${tableWithColumns.catalogId}|BN-${tableWithColumns.bucketName}|DN-${tableWithColumns.databaseName}|TN-${tableWithColumns.name}|R-${dp.region}`
          : `DS-glueLF|A-${tableWithColumns.catalogId}|DN-${tableWithColumns.databaseName}|TN-${tableWithColumns.name}|R-${dp.region}`,
    };
  }

  if (dataCellsFilter) {
    return {
      catalogId: dataCellsFilter.tableCatalogId,
      bucketName: dataCellsFilter.bucketName,
      databaseName: dataCellsFilter.databaseName,
      tableName: dataCellsFilter.tableName,
      hcDatasetId:
        dataCellsFilter.bucketName?.length > 0
          ? `DS-s3table|A-${dataCellsFilter.tableCatalogId}|BN-${dataCellsFilter.bucketName}|DN-${dataCellsFilter.databaseName}|TN-${dataCellsFilter.tableName}|R-${dp.region}`
          : `DS-glueLF|A-${dataCellsFilter.tableCatalogId}|DN-${dataCellsFilter.databaseName}|TN-${dataCellsFilter.tableName}|R-${dp.region}`,
      isFgaPolicy: true,
    };
  }

  if (lfTag)
    return {
      catalogId: lfTag.catalogId,
      tagKey: lfTag.expression[0].tagKey,
      tagValue: lfTag.expression[0].tagValues[0],
    };
  if (redshiftTag)
    return {
      catalogId: redshiftTag.catalogId,
      tagKey: redshiftTag.tagKey,
      tagValue: redshiftTag.tagValue,
    };
};

export const getInfoFromDP = (dp, infoName, catalogMap) => {
  if (!dp) return null;
  const dpDetails = getDataObjectDetails(dp);
  const defaultMessage = 'Visit data access permissions link for more details';
  const defaultBaselineDueDate = '2023-09-30';
  const defaultBaselineStatus = 'SCHEDULED';
  const defaultBaselineStatusReason =
    'Scheduled by Omni. All existing data access permissions granted before 2023-06-01' +
    ' require a review by the given due date.';

  switch (infoName) {
    case 'cardHeader':
    case 'grant':
      if (isValidWorkspace(dp.ownerId)) {
        return (
          <Link external={true} href={createWSPermissionLFLink(dp.dataPermissionId)}>
            View grant
          </Link>
        );
      } else {
        return (
          <Link external={true} href={createGroupPermissionLFLink(dp.dataPermissionId)}>
            View grant
          </Link>
        );
      }
    case 'catalog':
      if (DATA_PERMISSION_REDSHIFT_TYPE == dp.type) return dpDetails.catalogId;
      // for now baselining does not support DZ resources so we are not capturing clusterId / workgroupName
      // try once for a glue catalog name, else just default to account ID
      const key = keyForCatalogNameMap(getIdInfoFromId(dpDetails.hcDatasetId));
      if (!catalogMap.get(key)) return dpDetails.catalogId;
      return (
        <Link
          external={true}
          fontSize='body-m'
          href={createCatalogDetailLink(
            dpDetails.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
            dpDetails.catalogId,
            undefined,
            undefined,
            dpDetails.bucketName,
            dp.region,
          )}
        >
          {catalogMap.get(key)}
        </Link>
      );
    case 'database':
      if (!dpDetails.databaseName) return defaultMessage;
      return (
        <Link
          external={true}
          fontSize='body-m'
          href={createDatabaseDetailLink(
            dpDetails.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
            dpDetails.catalogId,
            undefined,
            undefined,
            dpDetails.bucketName,
            dpDetails.databaseName,
            dp.region,
          )}
        >
          {dpDetails.databaseName}
        </Link>
      );
    case 'table':
      if (!dpDetails?.tableName) return defaultMessage;
      if ('ALL_TABLES' == dpDetails.tableName)
        return (
          <>
            All datasets from database{' '}
            <Link
              external={true}
              href={createDatabaseDetailLink(
                dpDetails.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
                dpDetails.catalogId,
                undefined,
                undefined,
                dpDetails.bucketName,
                dpDetails.databaseName,
                dp.region,
              )}
            >
              {dpDetails.databaseName}
            </Link>{' '}
            by {dp?.audit?.DataOwnerId}
          </>
        );
      return (
        <>
          {dpDetails.isFgaPolicy != null && dpDetails.isFgaPolicy ? 'Fine grain dataset access ' : 'Dataset '}
          <Link external={true} fontSize='body-m' href={createDatasetDetailLink(dpDetails.hcDatasetId)}>
            {dpDetails.databaseName}.{dpDetails.tableName}
          </Link>{' '}
          by {dp?.audit?.DataOwnerId}
        </>
      );
    case 'permissionTag':
      if (dp.ownerId.startsWith('wks-') && dpDetails.tagKey && dpDetails.tagValue)
        return `${dpDetails.tagKey}.${dpDetails.tagValue}`;
      break;
    case 'principal':
      return dp.dataLakePrincipal;
    case 'baselineDueDate':
      if (!dp.baselineInfo.baselineDueDate) return defaultBaselineDueDate;
      return dp.baselineInfo.baselineDueDate;
    case 'baselineStatus':
      if (!dp.baselineInfo.baselineStatus) return defaultBaselineStatus;
      return dp.baselineInfo.baselineStatus;
    case 'baselineStatusReason':
      if (!dp.baselineInfo.baselineStatusReason) return defaultBaselineStatusReason;
      return dp.baselineInfo.baselineStatusReason;
    case 'lastBaselinedDate':
      return dp.baselineInfo.lastBaselineTimestamp;
    case 'lastBaselinedBy':
      return dp.baselineInfo.lastBaselineBy;
  }
};
